"use client";

import React, { useState } from "react";
import "../mpact2026.css";

type DayKey = "fri" | "sat" | "sun";

const DAYS: { key: DayKey; label: string; sub: string }[] = [
  { key: "fri", label: "Fri", sub: "25 Sep" },
  { key: "sat", label: "Sat", sub: "26 Sep" },
  { key: "sun", label: "Sun", sub: "27 Sep" },
];

function TimelineIconClock() {
  return (
    <svg
      className="mpact-tIcon"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="7" cy="18" r="2.4" stroke="currentColor" strokeWidth="1.3" />
      <circle cx="17" cy="16" r="2.4" stroke="currentColor" strokeWidth="1.3" />
      <path
        d="M9.4 18V6.5L19.4 4.5V14"
        stroke="currentColor"
        strokeWidth="1.3"
      />
    </svg>
  );
}

function TimelineIconGolf() {
  return (
    <svg
      className="mpact-tIcon"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M6 21V4"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
      <path
        d="M6 4L16 7.5L6 11"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      <ellipse
        cx="6"
        cy="21"
        rx="4"
        ry="1.1"
        stroke="currentColor"
        strokeWidth="1.2"
      />
    </svg>
  );
}

function Timeline({
  dayKey,
  children,
}: {
  dayKey: DayKey;
  children: React.ReactNode;
}) {
  return (
    <ol key={dayKey} className="mpact-timeline">
      {children}
    </ol>
  );
}

function TimelineItem({
  index,
  time,
  children,
}: {
  index: number;
  time: string;
  children: React.ReactNode;
}) {
  return (
    <li className="mpact-tIn" style={{ animationDelay: `${index * 0.04}s` }}>
      <span className="mpact-tTime">{time}</span>
      <div className="mpact-tBody">{children}</div>
    </li>
  );
}

const Agenda = () => {
  const [activeDay, setActiveDay] = useState<DayKey>("fri");

  return (
    <>
      <div className="mpact-dayTabs" role="tablist" aria-label="Retreat days">
        {DAYS.map((day) => (
          <button
            key={day.key}
            type="button"
            role="tab"
            aria-selected={activeDay === day.key}
            className={`mpact-dayTab ${activeDay === day.key ? "mpact-active" : ""}`}
            onClick={() => setActiveDay(day.key)}
          >
            {day.label}
            <span className="mpact-sub">{day.sub}</span>
          </button>
        ))}
      </div>

      {/* Friday */}
      <div
        className={`mpact-dayPanel ${activeDay === "fri" ? "mpact-active" : ""}`}
      >
        <p className="mpact-dayTheme">Arrive, connect &amp; set the tone</p>
        <p className="mpact-dayDesc">
          A relaxed opening evening designed to bring the group together and
          create the energy for Saturday&apos;s conversations.
        </p>

        {activeDay === "fri" && (
          <Timeline dayKey="fri">
            <TimelineItem index={0} time="Afternoon">
              <strong>Arrival, check-in &amp; welcome</strong>
            </TimelineItem>
            <TimelineItem index={1} time="3:00 – 5:15 PM">
              <strong>Welcome lounge &amp; informal introductions</strong>
            </TimelineItem>
            <TimelineItem index={2} time="5:30 – 7:00 PM">
              <strong>The Leadership Trial</strong>
              <p>
                An interactive leadership experience with Dr. Prateesh Nair.
              </p>
            </TimelineItem>
            <TimelineItem index={3} time="7:00 – 7:30 PM">
              <strong>Refresh &amp; transition</strong>
            </TimelineItem>
            <TimelineItem index={4} time="7:30 PM onwards">
              <strong>Welcome reception &amp; networking dinner</strong>
            </TimelineItem>
          </Timeline>
        )}
      </div>

      {/* Saturday */}
      <div
        className={`mpact-dayPanel ${activeDay === "sat" ? "mpact-active" : ""}`}
      >
        <p className="mpact-dayTheme">Talent. Technology. Transformation.</p>
        <p className="mpact-dayDesc">
          A day of hands-on learning, peer dialogue, talent strategy,
          recognition and celebration.
        </p>

        {activeDay === "sat" && (
          <Timeline dayKey="sat">
            <TimelineItem index={0} time="9:30 – 10:00 AM">
              <strong>Welcome address &amp; context setting</strong>
              <p>
                Talent, Technology and Transformation, presented by Varun Jain.
              </p>
            </TimelineItem>
            <TimelineItem index={1} time="10:00 – 11:30 AM">
              <strong>Hands-on AI: build your first AI agent</strong>
              <p>
                Experience the Miles Masterclass AI training solution with Rohan
                Singhai and Uttam Pai.
              </p>
            </TimelineItem>
            <TimelineItem index={2} time="11:30 AM – 12:30 PM">
              <strong>Panel discussion</strong>
              <div className="mpact-panelCard">
                <strong>
                  Scaling AI across the firm: leadership, adoption &amp; impact
                </strong>
                <p className="mpact-mod">
                  Moderator: <b>Varun Jain</b>
                </p>
                <ul className="mpact-panelists">
                  <li>
                    <b>Samit Shah</b>, Head of India Operations, Elliott Davis
                  </li>
                  <li>
                    <b>Shrenik Shah</b>, Managing Director, Armanino India LLP
                  </li>
                  <li>
                    <b>Vishnu Patwari</b>, National Managing Principal, BDO RISE
                  </li>
                  <li>
                    <b>Pratibha Chaurasia</b>, Partner in Charge India Office of
                    Grassi
                  </li>
                </ul>
              </div>
            </TimelineItem>
            <TimelineItem index={3} time="12:30 – 1:30 PM">
              <strong>Networking lunch</strong>
            </TimelineItem>
            <TimelineItem index={4} time="1:30 – 2:15 PM">
              <strong>
                The busy-season talent surge: a scalable workforce model
              </strong>
              <p>
                Miles Talent Solutions session with Ashish Jalan and Vasudha
                Dubey.
              </p>
            </TimelineItem>
            <TimelineItem index={5} time="2:15 – 3:15 PM">
              <strong>Panel discussion</strong>
              <div className="mpact-panelCard">
                <strong>
                  Building India&apos;s accounting talent ecosystem: capacity,
                  capability &amp; careers
                </strong>
                <p className="mpact-mod">
                  Moderator: <b>Ashish Jalan</b>
                </p>
                <ul className="mpact-panelists">
                  <li>
                    <b>Dr. Biju Toms</b>, Director, CHRIST University
                  </li>
                  <li>
                    <b>Ronak Shah</b>, Partner &amp; Country Manager, India,
                    Plante Moran
                  </li>
                  <li>
                    <b>Sumeet Aggarwal</b>, Partner-in-Charge, India Office, PKF
                    O&apos;Connor Davies
                  </li>
                  <li>
                    <b>Tim Moy</b>, Managing Partner, MBE CPAs
                  </li>
                  <li>
                    <b>Vishal Agarwal</b>, Managing Director &amp; Head of India
                    Offices, Citrin Cooperman
                  </li>
                </ul>
              </div>
            </TimelineItem>
            <TimelineItem index={6} time="3:15 – 3:30 PM">
              <strong>Coffee &amp; transition</strong>
            </TimelineItem>
            <TimelineItem index={7} time="3:30 – 4:30 PM">
              <strong>Rewards &amp; recognition</strong>
            </TimelineItem>
            <TimelineItem index={8} time="5:00 – 7:00 PM">
              <strong>Sundowner &amp; beach volleyball</strong>
            </TimelineItem>
            <TimelineItem index={9} time="7:30 – 10:00 PM">
              <strong>
                <TimelineIconClock />
                Dinner &amp; DJ party
              </strong>
            </TimelineItem>
          </Timeline>
        )}
      </div>

      {/* Sunday */}
      <div
        className={`mpact-dayPanel ${activeDay === "sun" ? "mpact-active" : ""}`}
      >
        <p className="mpact-dayTheme">Golf, conversations &amp; goodbyes</p>
        <p className="mpact-dayDesc">
          A relaxed final morning for shared experiences and informal
          relationship-building.
        </p>

        {activeDay === "sun" && (
          <Timeline dayKey="sun">
            <TimelineItem index={0} time="6:30 – 10:00 AM">
              <strong>Breakfast</strong>
              <p>Available before or after the golf experience.</p>
            </TimelineItem>
            <TimelineItem index={1} time="7:00 – 9:30 AM">
              <strong>
                <TimelineIconGolf />
                M-PACT golf experience: learn, swing &amp; play
              </strong>
              <p>
                Beginner-friendly coaching, golf fundamentals and a guided
                course experience.
              </p>
            </TimelineItem>
            <TimelineItem index={2} time="12:00 – 1:30 PM">
              <strong>Closing lunch &amp; informal networking</strong>
            </TimelineItem>
            <TimelineItem index={3} time="1:30 PM onwards">
              <strong>Departures</strong>
            </TimelineItem>
          </Timeline>
        )}
      </div>
    </>
  );
};

export default Agenda;
